import React from "react";

import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { PAGE_NAME, TICKETS_STATUS_OPTIONS } from "../../../../../constants/constants";
import {
    useAssignAssigneeMutation,
    useChangeRequestStatusMutation,
    useSingleRequestQuery,
} from "../../../../../store/requestManagement/requestSlice";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "../../../../atoms/skeleton";
import { formattedDateTime } from "../../../../../utils";
import { Dropdown } from "../../../../atoms/dropdown";
import { Dialog } from "../../../../atoms/dialog";
import { toast } from "react-toastify";
import { Modal } from "../../../../molecules/modal";
import { Loader } from "../../../../atoms/loader";
import { Comments } from "../../../../organisms/requestManagement/comments";
import { MultipleSelect } from "../../../../atoms/multipleSelect";
import { useActiveUserListQuery } from "../../../../../store/user/userSlice";
import { payloadType, ResponseType } from "../../../../../types";

const ApprovedRequestDetails = () => {
    const pageClassName = "p-approvedRequestDetails";
    const id = useParams<{ id: string }>().id;
    const navigate = useNavigate();

    const [changeStatusAPI, { isLoading: changeStatusLoading }] =
        useChangeRequestStatusMutation();

    const [isOpenComment, setIsOpenComment] = React.useState(false);
    const [comments, setComments] = React.useState<any[]>([]);
    const [requestId, setRequestId] = React.useState("");
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [reviewData, setReviewData] = React.useState({
        status: "",
        id: 0,
        comment: "",
    });

    const { data, isLoading, isError } = useSingleRequestQuery(id as string);
    const [assignAssigneesAPI, { isLoading: assigneeLoading }] = useAssignAssigneeMutation();
    const { data: userList, isLoading: usersLoading } = useActiveUserListQuery(
        {}
    );

    const [payload, setPayload] = React.useState<payloadType>({
        id: {
            value: id as string,
            error: "",
            isError: false,
        },
        assignees: {
            value: [] as { label: string; value: string }[],
            error: "",
            isError: false,
        },
    });

    React.useEffect(() => {
        setPayload({
            ...payload,
            assignees: {
                value: data?.result?.assignees?.length > 0 ? data?.result?.assignees?.map((assignee: any) => {
                    return {
                        label: assignee.middleName
                            ? `${assignee.firstName} ${assignee.middleName} ${assignee.lastName}`
                            : `${assignee.firstName} ${assignee.lastName}`,
                        value: assignee.id,
                    };
                }) : [],
                error: "",
                isError: false,
            },
        });
        if (isError) {
            navigate("/404", { replace: true });
            toast.error("Request not found");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isError]);

    const closeDialogHandler = () => {
        setIsDialogOpen(!isDialogOpen);
        setReviewData({
            status: "",
            id: 0,
            comment: "",
        });
    };

    const changeStatusHandler = async () => {
        setIsDialogOpen(!isDialogOpen);
        const fromData = new FormData();

        fromData.append("status", reviewData.status);
        fromData.append("id", reviewData.id.toString());
        fromData.append("comment", reviewData.comment);

        let response = (await changeStatusAPI(fromData)) as any;

        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            toast.error(response?.error?.data?.message);
        }
    };

    const assigneeHandler = async (assignees: { label: string, value: string }[]) => {
        const formData = new FormData();
        formData.append("id", id as string);
        if (assignees.length === 0)
            formData.append("assignees[]", assignees as any);
        else
            assignees.forEach((assignee) => {
                formData.append("assignees[]", assignee.value);
            });
        setPayload({
            ...payload,
            assignees: {
                value: assignees,
                error: "",
                isError: false,
            },
        });

        const response = (await assignAssigneesAPI(formData)) as ResponseType;
        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.data?.message);
            }
        }
    }

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>
                {PAGE_NAME.APPROVED_REQUEST_DETAILS}
            </p>
            <Loader isLoading={isLoading || assigneeLoading || usersLoading}>
                <div className={`${pageClassName}__container`}>
                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Request ID
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? <Skeleton height="small" /> : data?.result?.id}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Type</p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.type ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Sub Type</p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.subType ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>Status</p>
                        <p className={`${pageClassName}__container__item__status`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                data?.result?.status ?? "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Assignees
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : (
                                <MultipleSelect
                                    defaultOptions={userList?.result?.data?.map((user: any) => {
                                        return {
                                            label: user.firstName + " " + user.lastName,
                                            value: user.id,
                                        };
                                    })}
                                    defaultSelected={payload?.assignees?.value as { label: string; value: string }[]}
                                    placeholder="Please select assignees"
                                    onChangeHandler={assigneeHandler}
                                >
                                    <div className={`${pageClassName}__container__item__assignees`}>
                                        {(payload?.assignees?.value as { label: string; value: string }[])?.length > 0
                                            ? (payload?.assignees?.value as { label: string; value: string }[])?.map(
                                                (assignee: any, index: number) => (
                                                    <p key={index}>
                                                        {assignee.label} {index < (payload.assignees?.value as { label: string; value: string }[]).length - 1 ? "," : ""}
                                                    </p>
                                                )
                                            )
                                            : "Unassigned"}
                                    </div>
                                </MultipleSelect>
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Requester
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.requester ? (
                                data?.result?.requester?.middleName ? (
                                    `${data?.result?.requester?.firstName} ${data?.result?.requester?.middleName} ${data?.result?.requester?.lastName}`
                                ) : (
                                    `${data?.result?.requester?.firstName} ${data?.result?.requester?.lastName}`
                                )
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Support Ticket URL
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.supportTicketUrl ? (
                                <a
                                    href={data?.result?.supportTicketUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {data?.result?.supportTicketUrl}
                                </a>
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>

                    {data?.result?.notes && (
                        <div className={`${pageClassName}__container__item`}>
                            <p className={`${pageClassName}__container__item__title`}>Notes</p>
                            <p
                                style={{
                                    whiteSpace: 'pre-line',
                                    overflowWrap: 'break-word',
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-all'
                                }}
                                className={`${pageClassName}__container__item__value`}
                                dangerouslySetInnerHTML={{ __html: data?.result?.notes }}
                            />
                        </div>
                    )}

                    <div className={`${pageClassName}__container__item`}>
                        <p className={`${pageClassName}__container__item__title`}>
                            Created At
                        </p>
                        <p className={`${pageClassName}__container__item__value`}>
                            {isLoading ? (
                                <Skeleton height="small" />
                            ) : data?.result?.createdAt ? (
                                formattedDateTime(data?.result?.createdAt)
                            ) : (
                                "-"
                            )}
                        </p>
                    </div>
                    <p className={`${pageClassName}__container__userRequest__header`}>
                        Requests
                    </p>
                    <Loader isLoading={changeStatusLoading}>
                        <div
                            className={`${pageClassName}__container__userRequest ${data?.result?.subType === "Configuration"
                                ? `${pageClassName}__container__userRequest--configuration`
                                : ""
                                }`}
                        >
                            {data?.result?.requests?.map((request: any, index: number) => (
                                <div
                                    className={`${pageClassName}__container__userRequest__content`}
                                    key={index}
                                >
                                    <div
                                        className={`${pageClassName}__container__userRequest__content__container`}
                                    >
                                        {!["In Review", "Rejected", "Closed", "Canceled"].includes(request?.status) && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__content__action`}
                                            >
                                                <Dropdown
                                                    options={TICKETS_STATUS_OPTIONS(request?.status)}
                                                    selected={{ label: "Action", value: "" }}
                                                    placeholder="Review"
                                                    onClickChange={(e) => {
                                                        setIsDialogOpen(!isDialogOpen);
                                                        setReviewData({
                                                            ...reviewData,
                                                            status: e.value as string,
                                                            id: request?.id,
                                                        });
                                                    }}
                                                    isBordered
                                                    isIcon

                                                />
                                            </div>
                                        )}

                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Sub Request ID
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.id}
                                            </p>
                                        </div>

                                        {request?.firstName && request?.lastName && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    User Name
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.firstName} {request?.lastName}
                                                </p>
                                            </div>
                                        )}

                                        {request?.firstName && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    First Name
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.firstName}
                                                </p>
                                            </div>
                                        )}

                                        {request?.lastName && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Last Name
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.lastName}
                                                </p>
                                            </div>
                                        )}

                                        {request?.designation && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Designation
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.designation}
                                                </p>
                                            </div>
                                        )}

                                        {request?.address && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Address
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.address}
                                                </p>
                                            </div>
                                        )}

                                        {request?.organization && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Organization
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.organization?.name}
                                                </p>
                                            </div>
                                        )}

                                        {request?.department && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Department
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.department}
                                                </p>
                                            </div>
                                        )}

                                        {request?.section && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Section
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.section}
                                                </p>
                                            </div>
                                        )}

                                        {request?.projects?.length > 0 && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Projects
                                                </p>
                                                <div
                                                    className={`${pageClassName}__container__userRequest__item__list`}
                                                >
                                                    {request?.projects.map((project: any, index: number) => (
                                                        <li key={index}>{project}</li>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {request?.manager && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    PIC/Manager
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.manager?.middleName
                                                        ? `${request?.manager?.firstName} ${request?.manager?.middleName} ${request?.manager?.lastName} (${request?.manager?.email})`
                                                        : `${request?.manager?.firstName} ${request?.manager?.lastName} (${request?.manager?.email})`}
                                                </p>
                                            </div>
                                        )}

                                        {request?.email && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Email
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.email}
                                                </p>
                                            </div>
                                        )}

                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                SaaS Tool
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__value`}
                                            >
                                                {request?.saasTool}
                                            </p>
                                        </div>

                                        {request?.type && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Type
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.type}
                                                </p>
                                            </div>
                                        )}

                                        {request?.type && request?.name && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    {request?.type} Name
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.name}
                                                </p>
                                            </div>
                                        )}

                                        {request?.type && request?.newName && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    {request?.type} New Name
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.newName}
                                                </p>
                                            </div>
                                        )}

                                        {request?.role && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Role
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.role}
                                                </p>
                                            </div>
                                        )}

                                        {request?.templateType && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Template Type
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.templateType}
                                                </p>
                                            </div>
                                        )}

                                        {request?.approvers?.length > 0 && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Approvers
                                                </p>
                                                <div
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.approvers.map(
                                                        (approver: any, index: number) => (
                                                            <p key={index}>
                                                                {approver?.middleName
                                                                    ? `${approver?.firstName} ${approver?.middleName} ${approver?.lastName}`
                                                                    : `${approver?.firstName} ${approver?.lastName}`}
                                                            </p>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Status
                                            </p>
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__status`}
                                            >
                                                {request?.status}
                                            </p>
                                        </div>

                                        {request?.reviewedBy && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Reviewed By
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.reviewedBy?.middleName
                                                        ? `${request?.reviewedBy?.firstName} ${request?.reviewedBy?.middleName} ${request?.reviewedBy?.lastName}`
                                                        : `${request?.reviewedBy?.firstName} ${request?.reviewedBy?.lastName}`}
                                                </p>
                                            </div>
                                        )}

                                        {request?.issueSummary && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Issue Summary
                                                </p>
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                >
                                                    {request?.issueSummary}
                                                </p>
                                            </div>
                                        )}

                                        {request?.instruction && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Issue Description
                                                </p>
                                                <p
                                                    style={{
                                                        whiteSpace: 'pre-line',
                                                        overflowWrap: 'break-word',
                                                        wordWrap: 'break-word',
                                                        wordBreak: 'break-all'
                                                    }}
                                                    className={`${pageClassName}__container__userRequest__item__value`}
                                                    dangerouslySetInnerHTML={{
                                                        __html: request?.instruction,
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {request?.files && (
                                            <div
                                                className={`${pageClassName}__container__userRequest__item`}
                                            >
                                                <p
                                                    className={`${pageClassName}__container__userRequest__item__title`}
                                                >
                                                    Attachments
                                                </p>
                                                <div
                                                    className={`${pageClassName}__container__userRequest__item__files`}
                                                >
                                                    {request?.files.map((file: any, index: number) => (
                                                        <a
                                                            href={file.basePath + "/" + file?.filePath}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            key={index}
                                                            className={`${pageClassName}__container__userRequest__item__files__file`}
                                                        >
                                                            {file?.fileName}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            className={`${pageClassName}__container__userRequest__item`}
                                        >
                                            <p
                                                className={`${pageClassName}__container__userRequest__item__title`}
                                            >
                                                Comments
                                            </p>
                                            <div
                                                className={`${pageClassName}__container__userRequest__item__commentsContainer`}
                                            >
                                                <button
                                                    className={`${pageClassName}__container__userRequest__item__comments`}
                                                    onClick={() => {
                                                        setIsOpenComment(true);
                                                        setComments(request?.comments);
                                                        setRequestId(request?.id);
                                                    }}
                                                >
                                                    {request?.comments?.length}
                                                </button>
                                                <button
                                                    className={`${pageClassName}__container__userRequest__item__details`}
                                                    onClick={() => navigate(`/comments/${request?.id}`)}
                                                >
                                                    Details
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Loader>
                </div>
            </Loader>
            <Dialog
                header="Review Request"
                body={
                    <div className={`${pageClassName}__dialog`}>
                        <p className={`${pageClassName}__dialog__text`}>
                            Are you sure you want to {reviewData.status.toLowerCase()} this
                            request?
                        </p>
                        {/* <Textarea
                            className={`${pageClassName}__dialog__textarea`}
                            placeholder="Add a comment"
                            onChange={(e) => {
                                setReviewData({ ...reviewData, comment: e.target.value });
                            }}
                        /> */}
                    </div>
                }
                isOpen={isDialogOpen}
                onClose={closeDialogHandler}
                successFn={changeStatusHandler}
                btn={reviewData.status}
                icon={["Closed", "In Progress", "Waiting for Customer Response", "Fixed"].includes(reviewData.status) ? "approved" : "rejected"}
                color={["Closed", "In Progress", "Waiting for Customer Response", "Fixed"].includes(reviewData.status) ? "green" : undefined}
            />

            <Modal
                isOpen={isOpenComment}
                onClose={() => setIsOpenComment(false)}
                header="Comments"
            >
                <Comments
                    comments={comments}
                    setComments={setComments}
                    requestId={requestId}
                />
            </Modal>
        </div>
    );
};

export default ApprovedRequestDetails;
